import { Box, Stack, Typography } from "@mui/material";
import type { TransactionListDto } from "../../api/types";
import { formatTransactionCurrency } from "../../logic/currency-logic";
import { isNegative } from "../../logic/money";
import { useConfig } from "../../api/queries";

export type MatchRowProps = {
  transaction: TransactionListDto;
};

/**
 * Compact, read-only row used by `AutoTransactionMatchesDialog`. Shows the
 * fields relevant for visually confirming a rule still hits the expected
 * transactions: date, merchant + amount, then a secondary line with
 * the description when present.
 */
export const MatchRow = ({ transaction }: MatchRowProps) => {
  const { data: config } = useConfig();
  const fmt = config?.currencyFormat ?? "Nok";

  return (
    <Box
      sx={{
        py: 1,
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "baseline", justifyContent: "space-between" }}
      >
        <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
          >
            {transaction.datePaid}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {transaction.merchant}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          sx={{
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
            color: isNegative(transaction.amount)
              ? "error.main"
              : "success.main",
          }}
        >
          {formatTransactionCurrency(transaction.amount, fmt)}
        </Typography>
      </Stack>
      {transaction.description && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {transaction.description}
        </Typography>
      )}
    </Box>
  );
};
